// Apple-style confirm box, in place of the browser's confirm(). The button names the
// action ("Delete", "Sign out") instead of "OK". Resolves true on confirm, false on
// cancel, tapping outside, or Escape.

export function confirmBox({ title, message = '', confirmLabel = 'OK', cancelLabel = 'Cancel', danger = false }) {
  return new Promise((resolve) => {
    const prevFocus = document.activeElement;
    const wrap = document.createElement('div');
    wrap.className = 'fixed inset-0 z-[300] flex items-center justify-center bg-black/30 px-10';
    wrap.innerHTML = `
      <div role="alertdialog" aria-modal="true" aria-labelledby="grit-dlg-t" class="w-full max-w-[290px] rounded-[18px] overflow-hidden text-center"
        style="background:rgba(242,242,247,0.96);-webkit-backdrop-filter:blur(30px);backdrop-filter:blur(30px);animation:grit-dlg-in 220ms cubic-bezier(0.32,0.72,0,1)">
        <div class="px-5 pt-5 pb-4">
          <div id="grit-dlg-t" class="text-[17px] font-semibold text-ink leading-snug"></div>
          ${message ? '<div id="grit-dlg-m" class="text-[13px] text-ink mt-1 leading-snug"></div>' : ''}
        </div>
        <div class="flex border-t border-hairline">
          <button data-no class="flex-1 min-h-[46px] text-[17px] text-grit active:bg-fill"></button>
          <button data-yes class="flex-1 min-h-[46px] text-[17px] font-semibold border-l border-hairline active:bg-fill ${danger ? 'text-danger' : 'text-grit'}"></button>
        </div>
      </div>
      <style>@keyframes grit-dlg-in{from{opacity:0;transform:scale(1.08)}to{opacity:1;transform:scale(1)}}
      @media (prefers-reduced-motion: reduce){[role=alertdialog]{animation:none!important}}</style>`;
    wrap.querySelector('#grit-dlg-t').textContent = title;
    if (message) wrap.querySelector('#grit-dlg-m').textContent = message;
    wrap.querySelector('[data-no]').textContent = cancelLabel;
    wrap.querySelector('[data-yes]').textContent = confirmLabel;
    const done = (v) => { document.removeEventListener('keydown', onKey); wrap.remove(); prevFocus?.focus?.(); resolve(v); };
    const onKey = (e) => { if (e.key === 'Escape') done(false); };
    wrap.querySelector('[data-no]').onclick = () => done(false);
    wrap.querySelector('[data-yes]').onclick = () => done(true);
    wrap.addEventListener('click', (e) => { if (e.target === wrap) done(false); });
    document.addEventListener('keydown', onKey);
    document.body.appendChild(wrap);
    wrap.querySelector('[data-yes]').focus();
  });
}

// Apple-style choice box: a title, an optional line, and two or three named choices
// stacked as buttons. Resolves the chosen id, or null on Cancel / tap outside / Escape.
export function chooseBox({ title, message = '', options = [], cancelLabel = 'Cancel' }) {
  return new Promise((resolve) => {
    const wrap = document.createElement('div');
    wrap.className = 'fixed inset-0 z-[300] flex items-center justify-center bg-black/30 px-10';
    wrap.innerHTML = `
      <div role="alertdialog" aria-modal="true" class="w-full max-w-[290px] rounded-[18px] overflow-hidden text-center"
        style="background:rgba(242,242,247,0.96);-webkit-backdrop-filter:blur(30px);backdrop-filter:blur(30px)">
        <div class="px-5 pt-5 pb-4"><div data-t class="text-[17px] font-semibold text-ink leading-snug"></div>${message ? '<div data-m class="text-[13px] text-ink mt-1 leading-snug"></div>' : ''}</div>
        ${options.map((o, i) => `<button data-opt="${i}" class="w-full min-h-[46px] text-[17px] border-t border-hairline active:bg-fill ${i === 0 ? 'font-semibold text-grit' : 'text-grit'}"></button>`).join('')}
        <button data-no class="w-full min-h-[46px] text-[17px] text-grit border-t border-hairline active:bg-fill"></button>
      </div>`;
    wrap.querySelector('[data-t]').textContent = title;
    if (message) wrap.querySelector('[data-m]').textContent = message;
    options.forEach((o, i) => { wrap.querySelector(`[data-opt="${i}"]`).textContent = o.label; });
    wrap.querySelector('[data-no]').textContent = cancelLabel;
    const done = (v) => { document.removeEventListener('keydown', onKey); wrap.remove(); resolve(v); };
    const onKey = (e) => { if (e.key === 'Escape') done(null); };
    wrap.querySelectorAll('[data-opt]').forEach((b) => { b.onclick = () => done(options[+b.dataset.opt].id); });
    wrap.querySelector('[data-no]').onclick = () => done(null);
    wrap.addEventListener('click', (e) => { if (e.target === wrap) done(null); });
    document.addEventListener('keydown', onKey);
    document.body.appendChild(wrap);
  });
}
